import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch"; 

export function useHerKart() {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();
  const token = authState.token;

  const fetchCart = async () => {
    const config = { url: "/herkart/cart", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const syncCart = async (items) => {
    const config = {
      url: "/herkart/cart/sync",
      method: "post",
      data: { items },
      headers: { Authorization: token },
    };
    return fetchData(config, { silent: true });
  };

  const fetchSaved = async () => {
    const config = { url: "/herkart/saved", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const syncSaved = async (items) => {
    const config = {
      url: "/herkart/saved/sync",
      method: "post",
      data: { items },
      headers: { Authorization: token },
    };
    return fetchData(config, { silent: true });
  };

  const placeOrder = async (payload) => {
    const config = {
      url: "/herkart/orders",
      method: "post",
      data: payload,
      headers: { Authorization: token },
    };
    return fetchData(config, { silent: false });
  };

  const fetchMyOrders = async () => {
    const config = { url: "/herkart/orders/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const fetchAllOrders = async () => {
    const config = { url: "/herkart/orders/all", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const fetchOrderByNo = async (orderNo) => {
    const config = { url: `/herkart/orders/${orderNo}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const updateOrder = async (orderNo, payload) => {
    const config = {
      url: `/herkart/orders/${orderNo}`,
      method: "put",
      data: payload,
      headers: { Authorization: token },
    };
    return fetchData(config, { silent: false });
  };

  // Product Management

  const fetchLiveProducts = async () => {
    const config = { url: "/herkart/products/live", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const fetchMyProducts = async () => {
    const config = { url: "/herkart/products/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createProduct = async (payload) => {
    const config = { url: "/herkart/products", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateProduct = async (id, payload) => {
    const config = { url: `/herkart/products/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const deactivateProduct = async (id) => {
    const config = { url: `/herkart/products/${id}/deactivate`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // Stores
  const fetchMyStores = async () => {
    const config = { url: "/herkart/stores/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createStore = async (payload) => {
    const config = { url: "/herkart/stores", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateStore = async (id, payload) => {
    const config = { url: `/herkart/stores/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const deactivateStore = async (id) => {
    const config = { url: `/herkart/stores/${id}/deactivate`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const fetchStoreByCode = async (storeCode) => {
    const config = { url: `/herkart/stores/by-code/${storeCode}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const fetchLiveStores = async () => {
    const config = { url: "/herkart/stores/live", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  // Feedback
  const submitFeedback = async (payload) => {
    const config = { url: "/herkart/feedback", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
  const fetchMyFeedback = async () => {
    const config = { url: "/herkart/feedback/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const replyToFeedback = async (id, sellerReply) => {
    const config = { url: `/herkart/feedback/${id}/reply`, method: "put", data: { sellerReply }, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const fetchFeedbackByRef = async (level, refCode) => {
    const config = { url: `/herkart/feedback/by-ref/${level}/${refCode}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  // Sample/Demo requests
  const submitSampleRequest = async (payload) => {
    const config = { url: "/herkart/sample-requests", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
  const fetchMySampleRequests = async () => {
    const config = { url: "/herkart/sample-requests/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const updateSampleRequest = async (id, payload) => {
    const config = { url: `/herkart/sample-requests/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // Services
  const fetchLiveServices = async () => {
    const config = { url: "/herkart/services/live", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const fetchMyServices = async () => {
    const config = { url: "/herkart/services/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const createService = async (payload) => {
    const config = { url: "/herkart/services", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
  const updateService = async (id, payload) => {
    const config = { url: `/herkart/services/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
  const deactivateService = async (id) => {
    const config = { url: `/herkart/services/${id}/deactivate`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const bookService = async (payload) => {
    const config = { url: "/herkart/service-bookings", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
  const fetchMyServiceBookings = async () => {
    const config = { url: "/herkart/service-bookings/mine", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const fetchProviderServiceBookings = async () => {
    const config = { url: "/herkart/service-bookings/provider", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const fetchServiceBookingByNo = async (bookingNo) => {
    const config = { url: `/herkart/service-bookings/${bookingNo}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };
  const updateServiceBooking = async (bookingNo, payload) => {
    const config = { url: `/herkart/service-bookings/${bookingNo}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  return {
    fetchCart, syncCart,
    fetchSaved, syncSaved,
    placeOrder, fetchMyOrders, fetchAllOrders, fetchOrderByNo, updateOrder,
    fetchLiveProducts, fetchMyProducts, createProduct, updateProduct, deactivateProduct,
    fetchMyStores, createStore, updateStore, deactivateStore, fetchStoreByCode, fetchLiveStores,
    submitFeedback, fetchMyFeedback, replyToFeedback, fetchFeedbackByRef,
    submitSampleRequest, fetchMySampleRequests, updateSampleRequest,
    fetchLiveServices, fetchMyServices, createService, updateService, deactivateService,
    bookService, fetchMyServiceBookings, fetchProviderServiceBookings, fetchServiceBookingByNo, updateServiceBooking
  };
}